import React from 'react';
import { formatSimpleDate } from '../utils/dateFormatter';
import { getPriorityTextClass, formatPriorityLabel } from '../utils/priorityColors';

const ProblemCard = ({ problem, onClick }) => {
  const getDomainStyle = (domain) => {
    const d = domain?.toLowerCase() || '';
    switch (d) {
      case 'app_development': return 'text-white bg-white/5 px-2 py-0.5 rounded border border-white/20';
      case 'frontend': return 'text-gray-300 border border-white/10 px-2 py-0.5 rounded';
      case 'backend': return 'text-gray-300 border border-white/10 px-2 py-0.5 rounded';
      case 'cloud': return 'text-gray-400 border border-white/10 px-2 py-0.5 rounded';
      default: return 'text-gray-400';
    }
  };

  const formatDomain = (domain) => {
    if (!domain) return '';
    return domain
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Not Decided': return 'text-gray-500';
      case 'In Progress': return 'text-white bg-white/10 px-2 py-0.5 rounded border border-white/20';
      case 'Hold': return 'text-gray-400 italic';
      case 'Done': return 'text-white bg-white/20 px-2 py-0.5 rounded border border-white/40 font-bold';
      default: return 'text-gray-400';
    }
  };

  const getPriorityStyle = (priority) => {
    return getPriorityTextClass(priority);
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };



  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    return due < today;
  };

  // Handle both old localStorage format and new Supabase format
  const getReportedDate = () => {
    return problem.created_at || problem.date_reported || problem.dateReported || '';
  };

  const getDueDate = () => {
    return problem.due_date || problem.dueDate || '';
  };

  const formatCardDate = (value) => {
    if (!value) return null;

    const formatted = formatSimpleDate(value);
    if (formatted === 'Invalid Date' || formatted === 'Date Error' || formatted === 'N/A') {
      return null;
    }

    return formatted;
  };

  const getDueDateStyle = () => {
    const dueDate = getDueDate();
    if (!dueDate) return 'text-gray-400';
    
    // If task is completed, use normal gray color
    if (problem.status === 'Done') {
      return 'text-gray-400';
    }
    
    // If task is not completed, check if overdue
    // Task is overdue only after today ends (after 11:59 PM)
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const due = new Date(dueDate);
    due.setHours(23, 59, 59, 999);
    
    if (due < today) {
      return 'text-white font-bold bg-white/10 border border-white/20 px-2 py-0.5 rounded'; 
    } else {
      return 'text-gray-500'; 
    }
  };

  return (
    <div
      onClick={onClick}
      className="glass-card p-6 cursor-pointer hover:bg-white/5 transition-colors"
      data-testid={`problem-card-${problem.id}`}
    >
      {/* Title */}
      <h3 className="text-lg font-semibold mb-3 line-clamp-2 text-white" data-testid="problem-title">
        {problem.title}
      </h3>

      {/* Description */}
      <p className="text-gray-400 text-sm mb-4 line-clamp-3" data-testid="problem-description">
        {problem.description}
      </p>

      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-4 text-xs">
        <span className={`font-medium ${getDomainStyle(problem.domain)}`} data-testid="problem-domain">
          {formatDomain(problem.domain)}
        </span>
        <span className="text-gray-600">•</span>
        <span className={`font-medium ${getStatusStyle(problem.status)}`} data-testid="problem-status">
          {formatStatus(problem.status)}
        </span>
        <span className="text-gray-600">•</span>
        <span className={`font-medium ${getPriorityStyle(problem.priority)}`} data-testid="problem-priority">
          {formatPriorityLabel(problem.priority)}
        </span>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-sm text-gray-400">
        {formatCardDate(getReportedDate()) ? (
          <span data-testid="problem-date" className="text-xs text-gray-400">
            Reported: {formatCardDate(getReportedDate())}
          </span>
        ) : (
          <span data-testid="problem-date" className="text-xs text-gray-500">Reported: N/A</span>
        )}
        {getDueDate() && (
          <div className={`text-xs ${getDueDateStyle()}`} data-testid="problem-due-date">
            Due: {formatSimpleDate(getDueDate())}
          </div>
        )}
      </div>

      {/* Updated Date - Centered at bottom */}
      {problem.updated_at && (
        <div className="flex justify-center mt-3 pt-3 border-t border-white/10">
          <span className="text-xs text-gray-500" data-testid="problem-updated">
            Updated: {new Intl.DateTimeFormat('en-US', {
              month: 'short',
              day: '2-digit'
            }).format(new Date(problem.updated_at))}
          </span>
        </div>
      )}
    </div>
  );
};

export default ProblemCard;
